import { useCallback, useEffect, useState } from "react";
import { Text, Button, Tooltip } from "rizzui";
import { LuPhoneCall } from "react-icons/lu";
import { BiSolidLike, BiSolidDislike } from "react-icons/bi";
import {
  EOTPStatus,
  IEnquiryEntry,
  LeadFeedbackStatus,
} from "@/utilities/types/EnquiryEntry";
import { Roles } from "@/utilities/types/Users";
import formatPhone from "@/utilities/functions/format-phone";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";
import { getLeadSourceDisplay } from "@/utilities/functions/getLeadSourceDisplay";
import {
  getLeadTag,
  getLeadTagTooltip,
} from "@/utilities/functions/getLeadTag";
import { EnquiryEntryDataForm, LeadInsights, NotesSection } from "../details";
import { ContactModal } from "../modals";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import toast from "react-hot-toast";
import PhoneIcon from "@/components/icons/phone";
import { RiWhatsappLine } from "react-icons/ri";
import { IoIosMail } from "react-icons/io";
import ActivityHistory from "../details/ActivityHistory";
import { VscVerifiedFilled } from "react-icons/vsc";
import { MdOutlineInfo } from "react-icons/md";

// Constants for better maintainability
const ICON_SIZE = 20;

interface LeadDetailsPanelProps {
  selectedLead: IEnquiryEntry | null;
  userRole?: Roles;
  onRefresh?: () => void;
  renderCloseButton?: () => React.ReactNode;
  isReadOnly?: boolean;
  token?: string;
}

export default function LeadDetailsPanel({
  selectedLead,
  userRole,
  onRefresh = () => {},
  renderCloseButton,
  isReadOnly = false,
  token = "",
}: LeadDetailsPanelProps) {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [hasTaskPending, setHasTaskPending] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleCallClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `tel:${selectedLead?.phone}`;
  };

  const handleFeedback = useCallback(
    async (id: string, type: LeadFeedbackStatus) => {
      try {
        setIsSubmittingFeedback(true);
        await EnquiryEntriesRepository.updateFeedback(id, type);
        setHasTaskPending(false);
        toast.success("Feedback Submitted");
      } catch (error) {
        toast.error("Failed to submit feedback");
      } finally {
        setIsSubmittingFeedback(false);
      }
    },
    [selectedLead],
  );

  const handleWhatsAppClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(`https://wa.me/${selectedLead?.phone}`, "_blank");
    },
    [selectedLead?.phone],
  );

  useEffect(() => {
    setHasTaskPending(!selectedLead?.feedbackStatus);
  }, [selectedLead]);

  if (!selectedLead) {
    return null;
  }

  return (
    <>
      <div className="h-full overflow-y-auto bg-white">
        {/* Black Header with Lead Contact Info */}
        <div className="bg-black text-white p-6 relative md:rounded-t-md">
          <div className="flex flex-col justify-between items-start gap-4 md:flex-row">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="rounded-full flex items-center justify-center relative">
                  <span className="text-5xl">🔥</span>
                </div>
                <div>
                  <Text className="text-white font-semibold text-base md:text-xl">
                    {selectedLead.name}
                  </Text>
                  <div className="flex items-center gap-1">
                    <Text className="text-gray-300 text-base md:text-xl">
                      This is a{" "}
                      {selectedLead.contactSaleSubmissionReport
                        ? "Spam Lead"
                        : getLeadTag(selectedLead.tags?.[0] || "")}
                      .
                    </Text>
                    <Tooltip
                      size="md"
                      content={
                        <div className="max-w-52 text-sm text-left">
                          {selectedLead.contactSaleSubmissionReport
                            ? `This lead
                          submitted a suspicious form.`
                            : getLeadTagTooltip(selectedLead.tags?.[0] || "")}
                        </div>
                      }
                      color="invert"
                    >
                      <span>
                        <MdOutlineInfo
                          className="text-white"
                          size={ICON_SIZE}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Text className="text-gray-300 text-base md:text-xl">
                    <IoIosMail />
                  </Text>
                  <Text className="text-white text-base md:text-xl">
                    {selectedLead.email}
                  </Text>
                </div>
                <div className="flex items-center gap-2">
                  <Text className="text-gray-300 text-base md:text-xl">
                    <LuPhoneCall />
                  </Text>
                  <div className="flex gap-1 items-center">
                    <Text className="text-white text-base md:text-xl">
                      {formatPhone(selectedLead.phone, "+65")}
                    </Text>
                    {selectedLead.verificationStatus ===
                      EOTPStatus.VERIFIED && (
                      <Tooltip
                        size="md"
                        content="Lead Verified via OTP"
                        placement="top"
                        color="invert"
                      >
                        <span>
                          <VscVerifiedFilled className="text-blue-400 w-[18px] h-[18px]" />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-shrink-0 flex-row items-center justify-center rounded-full overflow-hidden h-[54px] min-w-[90px] bg-green-500 text-white">
              <Button
                variant="text"
                size="lg"
                className="flex gap-1 flex-col items-center justify-center h-full"
                onClick={handleCallClick}
              >
                <PhoneIcon className="w-4 h-4" />
                <span className="text-xs">Call</span>
              </Button>
              <Button
                size="lg"
                variant="text"
                className="flex gap-1 flex-col items-center justify-center h-full pl-0"
                onClick={(e) => {
                  if (isReadOnly) {
                    handleWhatsAppClick(e);
                  } else {
                    setIsContactModalOpen(true);
                  }
                }}
              >
                <RiWhatsappLine size={ICON_SIZE} />
                <span className="text-xs">WhatsApp</span>
              </Button>
            </div>
          </div>

          <div className="mt-4 text-left md:text-right">
            <Text className="text-gray-300 text-xs md:text-base">
              <span className="min-w-fit font-semibold">
                {convertToSingaporeTime(new Date(selectedLead.createdAt))}
              </span>
              <span className="mx-1">•</span>
              <span className="min-w-fit">
                {getLeadSourceDisplay(selectedLead)}
              </span>
            </Text>
          </div>
          {renderCloseButton && (
            <div className="absolute top-3 right-4">{renderCloseButton()}</div>
          )}
        </div>

        {/* Pending Task Section */}
        {userRole !== Roles.ADMIN &&
          !isReadOnly &&
          hasTaskPending &&
          !selectedLead.feedbackStatus && (
            <div className="bg-blue-50 px-6 py-3">
              <div className="flex justify-between items-center gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Text className="font-semibold text-gray-900 text-xl md:text-2xl ">
                      Pending Task
                    </Text>
                  </div>
                  <Text className="text-gray-700 text-sm">
                    Please confirm whether you were able to contact the lead.
                  </Text>
                </div>

                {/* Feedback buttons */}
                <div className="flex flex-shrink-0 flex-row items-center justify-center rounded-full overflow-hidden h-[54px] min-w-fit bg-white">
                  <Button
                    variant="text"
                    size="lg"
                    className="flex flex-col items-center justify-center h-full !text-green-600"
                    onClick={() => {
                      if (
                        selectedLead.feedbackStatus === "good" ||
                        isSubmittingFeedback
                      )
                        return;
                      handleFeedback(selectedLead.id, "good");
                    }}
                  >
                    <BiSolidLike size={ICON_SIZE} />
                    <Text className="text-xs font-semibold">Success</Text>
                  </Button>
                  <Button
                    size="lg"
                    variant="text"
                    className="flex flex-col items-center justify-center h-full pl-0 !text-red-600"
                    onClick={() => {
                      if (
                        selectedLead.feedbackStatus === "bad" ||
                        isSubmittingFeedback
                      )
                        return;
                      handleFeedback(selectedLead.id, "bad");
                    }}
                  >
                    <BiSolidDislike size={ICON_SIZE} />
                    <Text className="text-xs font-semibold">Failed</Text>
                  </Button>
                </div>
              </div>
            </div>
          )}

        {/* Lead Details */}
        <div className="p-3">
          <EnquiryEntryDataForm
            token={token}
            data={selectedLead}
            onRefresh={onRefresh}
            isReadOnly={isReadOnly}
          />
          <div className="border border-gray-100 w-full my-3" />
          <LeadInsights data={selectedLead} />
          <div className="border border-gray-100 w-full my-3" />
          <NotesSection
            data={selectedLead}
            isAdmin={userRole === Roles.ADMIN}
            onRefresh={onRefresh}
            isReadOnly={isReadOnly}
          />
          {!isReadOnly && <ActivityHistory lead={selectedLead} />}
        </div>
      </div>

      {/* Contact Modal */}
      {!isReadOnly && (
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          data={selectedLead}
        />
      )}
    </>
  );
}
